import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getClientId } from "@/lib/client";

export interface Room {
  id: string;
  code: string;
  mode: "game" | "poker" | "retro";
  host_id: string;
  status: "lobby" | "playing" | "revealing" | "finished";
  settings: { rounds?: number; timer?: number; category?: string; columns?: string[]; votesPerPlayer?: number };
  current_round_id: string | null;
  workspace_id?: string | null;
  max_players?: number;
}

export interface Player {
  id: string;
  room_id: string;
  client_id: string;
  name: string;
  team: string | null;
  is_host: boolean;
  score: number;
}

export interface Round {
  id: string;
  room_id: string;
  round_number: number;
  prompt: string;
  status: "collecting" | "revealed" | "finished";
  ends_at: string | null;
  results: any;
  refined?: boolean;
}

export interface Submission {
  id: string;
  round_id: string;
  player_id: string;
  value: string;
  column_key?: string | null;
}

export interface SubmissionVote {
  id: string;
  submission_id: string;
  player_id: string;
}

export interface SubmissionReaction {
  id: string;
  submission_id: string;
  player_id: string;
  emoji: string;
}

const reactionKey = (r: Pick<SubmissionReaction, "submission_id" | "player_id" | "emoji">) =>
  `${r.submission_id}|${r.player_id}|${r.emoji}`;

const dedupeReactions = (list: SubmissionReaction[]) => {
  const map = new Map<string, SubmissionReaction>();
  for (const r of list) map.set(reactionKey(r), r);
  return Array.from(map.values());
};

type ReactionSnapshotPayload = {
  submission_id: string;
  reactions: SubmissionReaction[];
};

export function useRoom(code: string | undefined) {
  const [room, setRoom] = useState<Room | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [round, setRound] = useState<Round | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [votes, setVotes] = useState<SubmissionVote[]>([]);
  const [reactions, setReactions] = useState<SubmissionReaction[]>([]);
  const [loading, setLoading] = useState(true);
  const roomChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const replaceReactionsForSubmission = useCallback(
    (submissionId: string, next: SubmissionReaction[]) => {
      setReactions((prev) => {
        const others = prev.filter((r) => r.submission_id !== submissionId);
        return dedupeReactions([...others, ...next]);
      });
    },
    [],
  );

  useEffect(() => {
    if (!code) return;
    let active = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const load = async () => {
      const { data: r } = await supabase
        .from("rooms")
        .select("*")
        .eq("code", code.toUpperCase())
        .maybeSingle();
      if (!active) return;
      if (!r) {
        setRoom(null);
        setLoading(false);
        return;
      }
      setRoom(r as Room);
      const { data: ps } = await supabase.from("players").select("*").eq("room_id", r.id);
      if (active) setPlayers((ps as Player[]) ?? []);
      if (r.current_round_id) {
        const { data: rd } = await supabase
          .from("rounds")
          .select("*")
          .eq("id", r.current_round_id)
          .maybeSingle();
        if (active) setRound((rd as Round) ?? null);
        const { data: subs } = await supabase
          .from("submissions")
          .select("*")
          .eq("round_id", r.current_round_id);
        if (active) setSubmissions((subs as Submission[]) ?? []);
        const subIds = (subs ?? []).map((s: any) => s.id);
        if (subIds.length) {
          const { data: vs } = await supabase
            .from("submission_votes")
            .select("*")
            .in("submission_id", subIds);
          if (active) setVotes((vs as SubmissionVote[]) ?? []);
          const { data: rxs } = await supabase
            .from("submission_reactions")
            .select("*")
            .in("submission_id", subIds);
          if (active) setReactions(dedupeReactions((rxs as SubmissionReaction[]) ?? []));
        } else {
          setVotes([]);
          setReactions([]);
        }
      } else {
        setRound(null);
        setSubmissions([]);
        setVotes([]);
        setReactions([]);
      }
      setLoading(false);

      channel = supabase
        .channel(`room-${r.id}`)
        .on("broadcast", { event: "reactions_snapshot" }, ({ payload }) => {
          const snap = payload as ReactionSnapshotPayload;
          if (!snap?.submission_id) return;
          setReactions((prev) => {
            const others = prev.filter((rx) => rx.submission_id !== snap.submission_id);
            return dedupeReactions([...others, ...(snap.reactions ?? [])]);
          });
        })
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "rooms", filter: `id=eq.${r.id}` },
          (payload) => {
            if (payload.eventType === "DELETE") setRoom(null);
            else setRoom(payload.new as Room);
          },
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "players", filter: `room_id=eq.${r.id}` },
          (payload) => {
            setPlayers((prev) => {
              if (payload.eventType === "INSERT") return [...prev, payload.new as Player];
              if (payload.eventType === "UPDATE")
                return prev.map((p) => (p.id === (payload.new as Player).id ? (payload.new as Player) : p));
              if (payload.eventType === "DELETE") return prev.filter((p) => p.id !== (payload.old as Player).id);
              return prev;
            });
          },
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "rounds", filter: `room_id=eq.${r.id}` },
          (payload) => {
            if (payload.eventType === "DELETE") return;
            const nr = payload.new as Round;
            setRound((cur) => {
              if (!cur) return nr;
              if (cur.id === nr.id) return nr;
              return cur;
            });
          },
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "submissions" },
          (payload) => {
            const sub = (payload.new ?? payload.old) as Submission;
            setSubmissions((prev) => {
              if (payload.eventType === "INSERT") {
                if (prev.some((s) => s.id === sub.id)) return prev;
                return [...prev, sub];
              }
              if (payload.eventType === "UPDATE")
                return prev.map((s) => (s.id === sub.id ? (payload.new as Submission) : s));
              if (payload.eventType === "DELETE") return prev.filter((s) => s.id !== sub.id);
              return prev;
            });
          },
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "submission_votes" },
          (payload) => {
            const v = (payload.new ?? payload.old) as SubmissionVote;
            setVotes((prev) => {
              if (payload.eventType === "INSERT") {
                if (prev.some((x) => x.id === v.id)) return prev;
                return [...prev, v];
              }
              if (payload.eventType === "DELETE") return prev.filter((x) => x.id !== v.id);
              return prev;
            });
          },
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "submission_reactions" },
          (payload) => {
            const rx = (payload.new ?? payload.old) as SubmissionReaction;
            if (!rx?.submission_id) return;
            setReactions((prev) => {
              if (payload.eventType === "INSERT") {
                const filtered = prev.filter((x) => x.id !== rx.id && reactionKey(x) !== reactionKey(rx));
                return [...filtered, rx];
              }
              if (payload.eventType === "DELETE") {
                return prev.filter((x) => x.id !== rx.id && reactionKey(x) !== reactionKey(rx));
              }
              return prev;
            });
          },
        )
        .subscribe();
      roomChannelRef.current = channel;
    };

    load();
    return () => {
      active = false;
      if (channel) supabase.removeChannel(channel);
      if (roomChannelRef.current === channel) roomChannelRef.current = null;
    };
  }, [code]);

  const publishReactionSnapshot = useCallback(
    (submissionId: string, snapshot: SubmissionReaction[]) => {
      replaceReactionsForSubmission(submissionId, snapshot);
      void roomChannelRef.current?.send({
        type: "broadcast",
        event: "reactions_snapshot",
        payload: { submission_id: submissionId, reactions: snapshot } satisfies ReactionSnapshotPayload,
      });
    },
    [replaceReactionsForSubmission],
  );

  useEffect(() => {
    if (!room?.current_round_id) {
      setRound(null);
      setSubmissions([]);
      setVotes([]);
      setReactions([]);
      return;
    }
    if (round?.id === room.current_round_id) return;
    let active = true;
    (async () => {
      const { data: rd } = await supabase
        .from("rounds")
        .select("*")
        .eq("id", room.current_round_id!)
        .maybeSingle();
      if (active && rd) setRound(rd as Round);
      const { data: subs } = await supabase
        .from("submissions")
        .select("*")
        .eq("round_id", room.current_round_id!);
      if (active) setSubmissions((subs as Submission[]) ?? []);
      const subIds = (subs ?? []).map((s: any) => s.id);
      if (subIds.length) {
        const { data: vs } = await supabase
          .from("submission_votes")
          .select("*")
          .in("submission_id", subIds);
        if (active) setVotes((vs as SubmissionVote[]) ?? []);
        const { data: rxs } = await supabase
          .from("submission_reactions")
          .select("*")
          .in("submission_id", subIds);
        if (active) setReactions(dedupeReactions((rxs as SubmissionReaction[]) ?? []));
      } else if (active) {
        setVotes([]);
        setReactions([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [room?.current_round_id]);

  const me = players.find((p) => p.client_id === getClientId()) ?? null;
  const amHost = !!me?.is_host;

  return {
    room,
    players,
    round,
    submissions,
    votes,
    reactions,
    me,
    amHost,
    loading,
    publishReactionSnapshot,
  };
}
